import React, { useState, useRef, useCallback, useEffect } from 'react';
import { makeStyles, tokens, Input } from '@fluentui/react-components';
import { EditRegular } from '@fluentui/react-icons';

// ── Props ──────────────────────────────────────────────────────────────────

interface InlineEditableTitleProps {
  value: string;
  onSave: (newValue: string) => void;
  /** Placeholder when empty */
  placeholder?: string;
}

// ── Styles ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  container: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusMedium,
    padding: '2px 6px',
    marginLeft: '-6px',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':hover > .edit-icon': {
      opacity: 1,
    },
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase500,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '600px',
  },
  editIcon: {
    opacity: 0,
    transition: 'opacity 0.15s ease',
    color: tokens.colorNeutralForeground3,
    fontSize: '14px',
    flexShrink: 0,
  },
  inputWrapper: {
    position: 'relative' as const,
    display: 'inline-grid',
    alignItems: 'center',
    maxWidth: '600px',
  },
  input: {
    minWidth: '120px',
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    gridArea: '1 / 1',
  },
  /** Hidden sizer span — mirrors input font so the grid cell auto-sizes */
  sizer: {
    gridArea: '1 / 1',
    visibility: 'hidden' as const,
    whiteSpace: 'pre',
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    /* account for input padding + border (~24px Fluent Input horizontal padding) */
    paddingLeft: '12px',
    paddingRight: '12px',
    pointerEvents: 'none' as const,
    maxWidth: '600px',
    overflow: 'hidden',
  },
});

// ── Component ──────────────────────────────────────────────────────────────

const InlineEditableTitle: React.FC<InlineEditableTitleProps> = ({
  value,
  onSave,
  placeholder = 'Untitled Dashboard',
}) => {
  const styles = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync external value changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
    }
  }, [value, isEditing]);

  const startEditing = useCallback(() => {
    setEditValue(value);
    setIsEditing(true);
  }, [value]);

  const save = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== value) {
      onSave(trimmed);
    } else {
      setEditValue(value); // revert
    }
    setIsEditing(false);
  }, [editValue, value, onSave]);

  const cancel = useCallback(() => {
    setEditValue(value);
    setIsEditing(false);
  }, [value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        save();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      }
    },
    [save, cancel],
  );

  const handleBlur = useCallback(() => {
    save();
  }, [save]);

  // Auto-focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <div className={styles.inputWrapper}>
        {/* Hidden sizer — forces the grid cell to match text width */}
        <span className={styles.sizer}>{editValue || placeholder}</span>
        <Input
          ref={inputRef}
          className={styles.input}
          value={editValue}
          onChange={(_, data) => setEditValue(data.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          size="large"
          appearance="underline"
          placeholder={placeholder}
        />
      </div>
    );
  }

  return (
    <div
      className={styles.container}
      onClick={startEditing}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          startEditing();
        }
      }}
      title="Click to rename"
    >
      <span className={styles.title}>{value || placeholder}</span>
      <EditRegular className={`${styles.editIcon} edit-icon`} />
    </div>
  );
};

export default InlineEditableTitle;